import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormCheckbox } from '@/components/form/FormCheckbox'
import { FormDatePicker } from '@/components/form/FormDatePicker'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { FormSelect } from '@/components/form/FormSelect'
import { FormTextarea } from '@/components/form/FormTextarea'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { StudentExperience } from '@/features/student/api/profile.api'
import { useExperienceMutations } from '@/features/student/hooks/useProfile'
import { formatDate, humanizeEnum } from '@/utils/format'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const EMPLOYMENT_TYPE_OPTIONS = [
  'full_time',
  'part_time',
  'internship',
  'contract',
  'freelance',
].map((value) => ({ value, label: humanizeEnum(value) }))

const schema = z.object({
  company_name: z.string().min(1, 'Company name is required'),
  job_title: z.string().min(1, 'Job title is required'),
  employment_type: z.string().min(1, 'Employment type is required'),
  location: z.string().optional(),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().optional(),
  is_current: z.boolean(),
  description: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

function toFormValues(item?: StudentExperience): FormValues {
  return {
    company_name: item?.companyName ?? '',
    job_title: item?.jobTitle ?? '',
    employment_type: item?.employmentType ?? '',
    location: item?.location ?? '',
    start_date: item?.startDate ?? '',
    end_date: item?.endDate ?? '',
    is_current: item?.isCurrent ?? false,
    description: item?.description ?? '',
  }
}

export function ExperienceSection({ items }: { items: StudentExperience[] }) {
  const [editing, setEditing] = useState<StudentExperience | 'new' | null>(null)
  const [apiError, setApiError] = useState<unknown>(null)
  const { create, update, remove } = useExperienceMutations()

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toFormValues() })

  useEffect(() => {
    reset(toFormValues(editing === 'new' || editing === null ? undefined : editing))
  }, [editing, reset])

  const isCurrent = watch('is_current')
  const isPending = create.isPending || update.isPending

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    const payload = {
      ...values,
      end_date: values.is_current ? undefined : values.end_date || undefined,
    }
    const onSettled = { onSuccess: () => setEditing(null), onError: (e: unknown) => setApiError(e) }
    if (editing === 'new') {
      create.mutate(payload, onSettled)
    } else if (editing) {
      update.mutate({ id: editing.id, payload }, onSettled)
    }
  })

  return (
    <div>
      {items.length === 0 && <p className="text-muted fs-13">No work experience added yet.</p>}
      {items.map((item) => (
        <div
          key={item.id}
          className="d-flex justify-content-between align-items-start border-bottom py-2"
        >
          <div>
            <p className="fw-medium mb-0">
              {item.jobTitle} · {item.companyName}
            </p>
            <p className="fs-12 text-muted mb-0">
              {humanizeEnum(item.employmentType)} · {formatDate(item.startDate)} —{' '}
              {item.isCurrent ? 'Present' : formatDate(item.endDate)}
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="ghost" onClick={() => setEditing(item)}>
              Edit
            </Button>
            <Button
              variant="danger"
              confirmTitle="Remove this experience?"
              onClick={() => remove.mutate(item.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
      <Button variant="secondary" className="mt-3" onClick={() => setEditing('new')}>
        Add Experience
      </Button>

      <Modal
        show={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add Experience' : 'Edit Experience'}
      >
        <FormErrorSummary
          errors={errors}
          serverErrors={getFieldErrors(apiError)}
          generalMessage={apiError ? getErrorMessage(apiError) : null}
        />
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <FormInput control={control} name="company_name" label="Company" required />
          <FormInput control={control} name="job_title" label="Job Title" required />
          <FormSelect
            control={control}
            name="employment_type"
            label="Employment Type"
            options={EMPLOYMENT_TYPE_OPTIONS}
            required
          />
          <FormInput control={control} name="location" label="Location" />
          <FormDatePicker control={control} name="start_date" label="Start Date" required />
          <FormCheckbox control={control} name="is_current" label="I currently work here" />
          {!isCurrent && <FormDatePicker control={control} name="end_date" label="End Date" />}
          <FormTextarea control={control} name="description" label="Description" />
          <Button type="submit" className="w-100" loading={isPending}>
            Save
          </Button>
        </form>
      </Modal>
    </div>
  )
}
